<template>
  <Main>
    <template v-slot:breadcrumb>
      <BaseBreadcrumb
        @return="previousPage"
        :items="breadcrumb"
        class="d-none d-sm-flex"
      />
    </template>
    <template v-slot:breadcrumb-mobile>
      <BaseBreadcrumb @return="previousPage" :items="breadcrumb" />
    </template>
    <h3 v-if="userAreaIsGarantia" class="primary--text mb-4">LIMITE DE CRÉDITO</h3>
    <v-row>
      <v-col cols="12" md="9" class="pr-md-5">
        <BaseFilter
          v-model="search"
          v-bind="filterProps"
          textPlaceholder="Buscar"
          @search="searchByFilter"
        />
      </v-col>
      <v-col cols="12" md="3" class="mb-xs-5 mb-sm-5">
        <BaseButton
          class="secondary text-uppercase subtitle-2 font-weight-bold"
          style="width: 100%"
          :to="{ name: 'economic_group credit_limit new' }"
        >
          {{ textButton }}
        </BaseButton>
      </v-col>
      <v-col cols="12" class="mt-5 mt-md-0">
        <BaseDataTable
          :headers="tableHeaders"
          :items="quotations.items"
          :server-items-length="quotations.total_count"
          no-data-text="Não há limites cadastrados para esse grupo econômico."
          loading-text="Carregando limites..."
          :options.sync="filterOptions"
          :loading="listLoading"
          class="eg-table"
          @click:row="goToCreditLimitConsult"
        >
          <template v-slot:[`item.seguradora.nome`]="{ item }">
            <div :class="borderColor(item.status)">
              {{ item.seguradora.nome }}
            </div>
          </template>
          <template v-slot:[`item.valor_limite_credito`]="{ item }">
            {{
              applyOrDefault(item, "valor_limite_credito", asMoneyString, "-")
            }}
          </template>
          <template v-slot:[`item.taxa_media`]="{ item }">
            {{ applyOrDefault(item, "taxa_media", asPercentageString, "-") }}
          </template>
          <template v-slot:[`item.status`]="{ item }">
            {{ item.status.nome }}
          </template>
        </BaseDataTable>
      </v-col>
    </v-row>
  </Main>
</template>

<script>
import _ from "lodash";
import { mapActions, mapState } from "vuex";
import { asMoneyString } from "@/utils/formats.js";
import { LoadingRequest } from "@/mixins/LoadingMixin";
import {
  CommonPayload as ResourceFilterMixin,
  defaultFilterOptions
} from "@/mixins/PayloadDataTableMixin";

const defaultFilterProps = {
  outlined: true,
  dense: true,
  "background-color": "white",
  class: "filter__icon",
  "append-outer-icon": "fa-search"
};

const tableHeadersGarantia = [
  { text: "Seguradora", value: "seguradora.nome", sortable: true },
  { text: "Limite de Crédito", value: "valor_limite_credito", sortable: true },
  { text: "Taxas", value: "taxa_media", sortable: false },
  { text: "Status", value: "status", sortable: true }
];

const defaultTableHeaders = [
  { text: "Seguradora", value: "seguradora.nome", sortable: true },
  { text: "Taxas", value: "taxa_media", sortable: false },
  { text: "Status", value: "status", sortable: true }
];

const colorByStatus = {
  aprovado: "lightGreen",
  recusado: "pink",
  nomeacao: "blue",
  nao_retornou: "lightBlue",
  expirado: "lightGray"
};
const DEFAULT_STATUS_COLOR = "yellow";

export default {
  name: "CreditLimitList",
  mixins: [LoadingRequest, ResourceFilterMixin],
  data: () => ({
    asMoneyString,
    asPercentageString: x => `${x.toString()} %`,
    types: { false: "TMD", true: "SSD" },
    status: { false: "Inativo", true: "Ativo" },
    statusFilterOption: null,
    filterProps: defaultFilterProps,
    filterOptions: defaultFilterOptions(),
    creditLimits: [
      {
        id: 1,
        seguradora: "Seguradora A",
        limite: null,
        taxas: null,
        status: {
          id: "em_analise",
          nome: "Em análise"
        }
      }
    ],
    currentTab: 0,
    search: "",
    listLoading: false,
    tableHeadersGarantia,
    defaultTableHeaders,
    defaultFilterProps,
    defaultFilterOptions
  }),
  created() {
    this.fetchData();
  },
  computed: {
    ...mapState("economic_group", [
      "economicGroup",
      "quotations",
      "creditLimitStatuses"
    ]),
    ...mapState("me", ["userAreaIsGarantia"]),
    groupId() {
      return this.$route.params.groupId;
    },
    breadcrumb() {
      const { nome } = this.economicGroup || { nome: "" };
      return [
        {
          text: `Grupo Econômico: ${nome}`,
          to: "economic_group view"
        }
      ];
    },
    textButton() {
      return this.userAreaIsGarantia ? "CONSULTAR MERCADO" : "HABILITAR SEGURADORAS";
    },
    tableHeaders() {
      return this.userAreaIsGarantia ? this.tableHeadersGarantia : this.defaultTableHeaders;
    }
  },
  methods: {
    ...mapActions("economic_group", [
      "findEconomicGroup",
      "selectCreditLimitConsult",
      "listQuotationsOfEconomicGroup",
      "listCreditLimitStatuses"
    ]),
    applyOrDefault(item, key, fn, defaultValue) {
      return _.has(item, key) && _.get(item, key)
        ? fn(_.get(item, key))
        : defaultValue;
    },
    previousPage() {
      this.$router.push({
        name: "economic_group view",
        params: { groupId: this.groupId }
      });
    },
    borderColor(item) {
      const status = (item && colorByStatus[item.id]) || DEFAULT_STATUS_COLOR;
      return `eg-table__border eg-table__border-${status}`;
    },
    goToCreditLimitConsult(item) {
      this.selectCreditLimitConsult(item);
      this.$router.push({
        name: "economic_group credit_limit consult",
        params: {
          groupId: this.groupId,
          consultId: item.id
        }
      });
    },
    performFilter(filter) {
      const { economicGroup, creditLimitStatuses, groupId } = this;
      const egPipe = !economicGroup
        ? this.findEconomicGroup(groupId)
        : Promise.resolve(economicGroup);
      const clPipe = !creditLimitStatuses
        ? this.listCreditLimitStatuses
        : () => Promise.resolve(creditLimitStatuses);
      this.withLoading(
        egPipe
          .then(clPipe)
          .then(() => this.listQuotationsOfEconomicGroup({ groupId, filter }))
      );
    },
    searchByFilter() {
      const options = {
        ...this.filterOptions,
        status: this.statusFilterOption && this.statusFilterOption.id
      };

      this.performFilter(this.toLSPFilter(options, this.search));
    },
    fetchData() {
      this.performFilter(null);
    }
  },
  watch: {
    filterOptions: {
      handler(value) {
        const options = {
          ...value,
          status: this.statusFilterOption && [this.statusFilterOption.id]
        };
        this.performFilter(this.toLSPFilter(options, this.search));
      },
      deep: true
    }
  }
};
</script>

<style lang="sass">
.eg-table
  .v-data-table__wrapper
    @media (min-width:960px)
      overflow: visible !important
  td
    position: relative

  &__border::before
    overflow: visible !important
    display: block
    position: absolute
    background-color: #CCC
    width: 5px
    border-radius: 2px
    top: 8px
    bottom: 8px
    left: 0px
    margin-left: -3px
    content: " "
  &__border-blue::before
    background-color: var(--v-secondary-base) !important
  &__border-yellow::before
    background-color: var(--v-yellow-base) !important
  &__border-lightGreen::before
    background-color: var(--v-lightGreen-base) !important
  &__border-pink::before
    background-color: var(--v-pink-base) !important
  &__border-lightBlue::before
    background-color: var(--v-lightBlue-base) !important
  &__border-lightGray::before
    background-color: var(--v-lightGray-base) !important
</style>
